import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../@core/auth/auth.service';
import { BulkFileResponse, JobStatusResponse, UserUploadFile, UserUploadFileMetadata } from '../models/userUploadFile';
import { retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {

  protected basePath = '/';

  public fileList: [] = [];
  public isLoading: boolean = true;
  private AuthToken: string;

  constructor(
    protected httpClient: HttpClient,
    private authService: AuthService
  ) {
    this.basePath = environment.PLATFORM_API_URL;
  }

  public setToken(token: string) {
    this.AuthToken = token;
  }

  public getFileUploads(id?: number): Observable<UserUploadFile> {
    if (id != null) { return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload/${id}`); }
    else { return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload`); }
  }

  public getFileUploadsByFieldEvent(id: number): Observable<UserUploadFile> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('fieldEventId', id.toString());

    return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload`, { params: queryParameters });
  }



  public getFileUploadRecords(lastUpdate: Date): Observable<UserUploadFileMetadata[]> {

    let queryParameters = new HttpParams();

    if (lastUpdate) queryParameters = queryParameters.set('lastUpdate', lastUpdate.toString());

    return this.httpClient.get<UserUploadFileMetadata[]>(`${this.basePath}/api/FileUpload/GetRecordMetadata`, { params: queryParameters }).pipe(retry(4));
  }



  public deleteFileUploads(id: number) {

    return this.httpClient.delete(`${this.basePath}/api/FileUpload/${id}`);
  }

  public postFileUploads(file: File[], fieldEventId?): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (fieldEventId)
      formData.append('fieldEventId', fieldEventId);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload`, formData);
  }

  public postLotFileUploads(file: File[], lotId?): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (lotId)
      formData.append('lotId', lotId);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/LotFiles`, formData);

  }


  public getLotFileUploads(id?: number): Observable<UserUploadFile> {

    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set('lotId', id.toString());

    if (id != null) { return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload/LotFiles`, { params: queryParameters }); }
    else { return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload/LotFiles`); }
  }


  public postLoadFileUploads(file: File[], loadId?): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (loadId)
      formData.append('loadId', loadId);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/LoadFiles`, formData);
  }

  public getLoadFileUploads(id?: number): Observable<UserUploadFile> {

    if (id != null) { return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload/${id}`); }
    else { return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload`); }
  }

  public getFileUploadsByLoad(id: number): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('loadId', id.toString());

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/LoadFiles`, { params: queryParameters });
  }

  public getFileUploadsByFeatureEventId(id: number): Observable<UserUploadFile> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('featureId', id.toString());

    return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload/FeatureFiles`, { params: queryParameters });
  }

  public postTaskWorkOrderFileUploads(file: File[], taskWorkOrderId?: number): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (taskWorkOrderId)
      formData.append('taskWorkOrderId', taskWorkOrderId.toString());

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/TaskWorkOrderFiles`, formData);
  }

  public getFileUploadsByTaskWorkOrder(id: number): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('taskWorkOrderId', id.toString());

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/TaskWorkOrderFiles`, { params: queryParameters });
  }

  public postFarmFileUploads(file: File[], farmId: number): Observable<any> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    formData.append('farmId', farmId.toString());

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/FarmFiles`, formData);
  }

  /** Metadata + thumbnails only (no file bytes). Use getFileById to load a file's content. */
  public getFarmFiles(farmId: number): Observable<UserUploadFile[]> {
    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/FarmFiles`, { params: new HttpParams().set('farmId', farmId.toString()) });
  }

  public getFileById(id: number): Observable<UserUploadFile> {
    return this.httpClient.get<UserUploadFile>(`${this.basePath}/api/FileUpload/GetFileById/${id}`);
  }

  public postFieldQCEventFileUploads(file: File[], fieldQCEventId?: number): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (fieldQCEventId)
      formData.append('fieldQCEventId', fieldQCEventId.toString());

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/FieldQCEventFiles`, formData);
  }

  public getFileUploadsByFieldQCEvent(id: number): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('fieldQCEventId', id.toString());

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/FieldQCEventFiles`, { params: queryParameters });
  }

  public postAuditRecordFileUploads(file: File[], auditRecordId?: number): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (auditRecordId)
      formData.append('auditRecordId', auditRecordId.toString());

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/AuditRecordFiles`, formData);
  }

  public getFileUploadsByAuditRecord(id: number): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('auditRecordId', id.toString());

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/AuditRecordFiles`, { params: queryParameters });
  }

  public postContractUploads(file: File[], contractId?): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (contractId)
      formData.append('contractId', contractId);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/ContractFiles`, formData);
  }
  public getContractFileUploads(id?): Observable<[UserUploadFile]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('contractId', id.toString());

    return this.httpClient.get<[UserUploadFile]>(`${this.basePath}/api/FileUpload/ContractFiles`,
      {
        params: queryParameters
      });
  }

  // Save PO files in db
  public postPOUploads(file: File[], poId?): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (poId)
      formData.append('poId', poId);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/PurchaseOrderFiles`, formData);
  }

  //Get PO files from db
  public getPurchaseOrderFiles(id?): Observable<[UserUploadFile]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('poId', id.toString());

    return this.httpClient.get<[UserUploadFile]>(`${this.basePath}/api/FileUpload/PurchaseOrderFiles`,
      {
        params: queryParameters
      });
  }

  // Upload spot product image
  public postSpotProductImages(file: File[], name: string): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    }

    if (name)
      formData.append('Name', name);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/SpotProductImages`, formData);
  }

  // Get spot product images
  public getSpotProductImages(name: string): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('Name', name);

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/SpotProductImages`,
      {
        params: queryParameters
      });
  }

  // Upload spot data sheet
  public postSpotDataSheets(file: File[], name: string): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    }

    if (name)
      formData.append('Name', name);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/SpotDataSheets`, formData);
  }

  // Get spot data sheets
  public getSpotDataSheets(name: string): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();

    queryParameters = queryParameters.set('Name', name);

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/SpotDataSheets`,
      {
        params: queryParameters
      });
  }

  // Upload packout files
  public postPackoutFileUploads(file: File[], packoutId?): Observable<FormData> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    };

    if (packoutId)
      formData.append('packoutId', packoutId);

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/PackoutFiles`, formData);
  }

  // Get packout files
  public getPackoutFileUploads(packoutId: number): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set('packoutId', packoutId.toString());

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/PackoutFiles`,
      { params: queryParameters }
    );
  }

  // Upload column name option image
  public postColumnNameOptionImage(file: File[], columnNameOptionId: number): Observable<any> {

    const formData = new FormData();

    for (const x of file) {
      formData.append('file', x);
    }

    if (columnNameOptionId)
      formData.append('columnNameOptionId', columnNameOptionId.toString());

    return this.httpClient.post<any>(`${this.basePath}/api/FileUpload/ColumnNameOptionImages`, formData);
  }

  // Get column name option image
  public getColumnNameOptionImage(columnNameOptionId: number): Observable<UserUploadFile[]> {

    let queryParameters = new HttpParams();
    queryParameters = queryParameters.set('columnNameOptionId', columnNameOptionId.toString());

    return this.httpClient.get<UserUploadFile[]>(`${this.basePath}/api/FileUpload/ColumnNameOptionImages`,
      { params: queryParameters }
    );
  }

  public getBulkFiles(fileIds: number[]): Observable<BulkFileResponse[]> {
    return this.httpClient.post<BulkFileResponse[]>(
      `${this.basePath}/api/FileUpload/BulkFiles`,
      { fileIds }
    );
  }

  public requestBulkFiles(fileIds: number[], targetKb?: number): Observable<{ jobId: string }> {
    const body: { fileIds: number[]; targetKb?: number } = { fileIds };
    if (targetKb != null) { body.targetKb = targetKb; }
    return this.httpClient.post<{ jobId: string }>(
      `${this.basePath}/api/FileUpload/BulkFiles/request`,
      body
    );
  }

  public checkJobStatus(jobId: string): Observable<JobStatusResponse> {
    return this.httpClient.get<JobStatusResponse>(
      `${this.basePath}/api/FileUpload/BulkFiles/status/${jobId}`
    );
  }

 public getJobResult(jobId: string): Observable<{ files: BulkFileResponse[] }> {
    return this.httpClient.get<{ files: BulkFileResponse[] }>(
      `${this.basePath}/api/FileUpload/BulkFiles/result/${jobId}`
    );
  }
}
